import type { ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { useGetPages } from '../../../hooks/useGetPages';

interface PagePickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: { title: string; href: string }) => void;
}

export const PagePickerModal = ({ open, onClose, onSelect }: PagePickerModalProps): ReactElement => {
  const [searchParams] = useSearchParams();
  const websiteId = searchParams.get('websiteId') ?? '';
  const { pages, isLoading } = useGetPages({ websiteId });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="vtwo:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Link to Page</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="vtwo:flex vtwo:justify-center vtwo:py-8">
            <CircularProgress size={24} />
          </div>
        )}

        {!isLoading && pages.length === 0 && (
          <p className="vtwo:text-sm vtwo:text-gray-500 vtwo:py-4 vtwo:text-center">
            No pages available
          </p>
        )}

        {!isLoading && pages.length > 0 && (
          <ul className="vtwo:divide-y vtwo:divide-gray-100 vtwo:max-h-96 vtwo:overflow-y-auto">
            {pages.map((page) => (
              <li
                key={page.id}
                className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:py-3 vtwo:px-1"
              >
                <div className="vtwo:flex vtwo:flex-col vtwo:min-w-0">
                  <span className="vtwo:text-sm vtwo:font-medium vtwo:text-gray-900 vtwo:truncate">
                    {page.name}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="vtwo:ml-4 vtwo:shrink-0"
                  onClick={() => {
                    onSelect({ title: page.name, href: page.href });
                    onClose();
                  }}
                >
                  <Plus className="vtwo:h-3 vtwo:w-3" />
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
